import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const NAV_DARK_COLOR = "#0F172A";

// Traffic source labels - must match backend SOURCE_MAP keys
const SOURCE_LABELS = {
  display: "Display",
  direct: "Direct",
  organic: "Organic Search",
  paid: "Paid Search",
  paid_social: "Paid Social",      // NEW: Separate from Paid Search
  unassigned: "Unassigned",
  paid_video: "Paid Video",
  referral: "Referral",
  email: "Email",
  organic_social: "Organic Social",
  ai_referral: "AI Referral",
  other_campaigns: "Other Campaigns",  // NEW: Was going to unassigned
  offline: "Offline Sources"           // NEW: Was going to unassigned
};

// Calculate percentage change
const calcDelta = (current, previous) => {
  if (!previous || previous === 0) {
    return current > 0 ? { value: "New", isPositive: true, isNew: true, numeric: 100 } : { value: "0%", isPositive: null, isNew: false, numeric: 0 };
  }
  const change = ((current - previous) / previous * 100);
  return {
    value: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
    isPositive: change > 0,
    isNew: false,
    numeric: change
  };
};

// Delta cell component
const DeltaCell = ({ delta }) => {
  if (delta.isNew) {
    return <span className="text-green-600 font-medium text-xs">New</span>;
  }
  if (delta.isPositive === null) {
    return <span className="text-slate-400">-</span>;
  }
  return (
    <span className={`font-medium text-xs flex items-center gap-0.5 ${delta.isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {delta.isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {delta.value}
    </span>
  );
};

// Single region panel with table AND chart
const RegionPanel = ({ 
  region, 
  regionLabel, 
  dateFilter, 
  excludeTurkey, 
  trafficDataSource,
  sessionToken,
  comparisonType
}) => {
  const { isPresentationMode, maskNumber } = usePresentationMode();
  const [currentData, setCurrentData] = useState(null);
  const [previousMoMData, setPreviousMoMData] = useState(null);
  const [previousYoYData, setPreviousYoYData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate comparison periods
  const { currentLabel, momLabel, yoyLabel, momDates, yoyDates } = useMemo(() => {
    if (!dateFilter?.startDate || !dateFilter?.endDate) {
      return { currentLabel: "", momLabel: "", yoyLabel: "", momDates: null, yoyDates: null };
    }

    const [startYear, startMonth, startDay] = dateFilter.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = dateFilter.endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    // Current period label - always include year on BOTH dates
    const currentLabel = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    
    // Previous Period - include year on BOTH dates
    const momStart = new Date(start);
    momStart.setDate(momStart.getDate() - daysDiff - 1);
    const momEnd = new Date(start);
    momEnd.setDate(momEnd.getDate() - 1);
    const momLabel = `${momStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${momEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    // YoY - include year on BOTH dates
    const yoyStart = new Date(startYear - 1, startMonth - 1, startDay);
    const yoyEnd = new Date(endYear - 1, endMonth - 1, endDay);
    const yoyLabel = `${yoyStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${yoyEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return {
      currentLabel,
      momLabel,
      yoyLabel,
      momDates: { start: formatDate(momStart), end: formatDate(momEnd) },
      yoyDates: { start: formatDate(yoyStart), end: formatDate(yoyEnd) }
    };
  }, [dateFilter]);

  // Fetch data for this specific region (date filter applied, but NOT region filter from parent)
  useEffect(() => {
    const fetchData = async () => {
      if (!sessionToken || !dateFilter?.startDate || !dateFilter?.endDate) return;
      setLoading(true);
      
      try {
        const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
        const baseParams = { 
          Authorization: `Bearer ${sessionToken}`,
          exclude_turkey: excludeTurkey,
          region: region  // Always use THIS region, not parent's filter
        };
        if (viewingTenantId) {
          baseParams["X-Tenant-ID"] = viewingTenantId;
        }

        const endpoint = trafficDataSource === "hubspot" 
          ? `${API}/integrations/analytics/hubspot/traffic-sources`
          : `${API}/integrations/analytics/ga4/traffic-sources`;

        // Current period (using parent's date filter)
        const currentResponse = await axios.get(endpoint, {
          params: { ...baseParams, start_date: dateFilter.startDate, end_date: dateFilter.endDate }
        });
        setCurrentData(currentResponse.data?.totals || {});

        // MoM comparison
        if (momDates) {
          const momResponse = await axios.get(endpoint, {
            params: { ...baseParams, start_date: momDates.start, end_date: momDates.end }
          });
          setPreviousMoMData(momResponse.data?.totals || {});
        }

        // YoY comparison
        if (yoyDates) {
          const yoyResponse = await axios.get(endpoint, {
            params: { ...baseParams, start_date: yoyDates.start, end_date: yoyDates.end }
          });
          setPreviousYoYData(yoyResponse.data?.totals || {});
        }

      } catch (error) {
        console.error(`Error fetching ${region} traffic data:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionToken, dateFilter, excludeTurkey, trafficDataSource, region, momDates, yoyDates]);

  // Build table data
  const buildTableData = (current, previous) => {
    if (!current) return [];
    
    // All sources - order matters for display
    const sources = ['direct', 'organic', 'paid', 'paid_social', 'organic_social', 'email', 'referral', 'ai_referral', 'display', 'paid_video', 'other_campaigns', 'offline', 'unassigned'];
    
    return sources.map(source => {
      const currentVal = current[source] || 0;
      const previousVal = previous?.[source] || 0;
      return {
        source,
        label: SOURCE_LABELS[source] || source,
        current: currentVal,
        previous: previousVal,
        delta: calcDelta(currentVal, previousVal)
      };
    }).filter(row => row.current > 0 || row.previous > 0);
  };

  // Build chart data
  const buildChartData = (current, previous) => {
    if (!current) return [];
    
    // Sources to show in chart
    const sources = ['direct', 'organic', 'paid', 'paid_social', 'organic_social', 'email', 'referral', 'ai_referral', 'display', 'other_campaigns', 'offline'];
    
    return sources
      .map(source => ({
        name: SOURCE_LABELS[source] || source,
        current: current[source] || 0,
        previous: previous?.[source] || 0
      }))
      .filter(item => item.current > 0 || item.previous > 0)
      .sort((a, b) => b.current - a.current);
  };

  const previousData = comparisonType === "mom" ? previousMoMData : previousYoYData;
  const previousLabel = comparisonType === "mom" ? momLabel : yoyLabel;
  const tableData = buildTableData(currentData, previousData);
  const chartData = buildChartData(currentData, previousData);
  const totalCurrent = tableData.reduce((sum, row) => sum + row.current, 0);
  const totalPrevious = tableData.reduce((sum, row) => sum + row.previous, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-3 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-semibold flex items-center gap-2" style={{ color: NAV_DARK_COLOR }}>
          <Globe className="w-4 h-4" />
          {regionLabel}
          {excludeTurkey && region === "emea" && (
            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
              No Turkey
            </span>
          )}
        </h4>
        <span className="text-xs text-slate-500">
          {currentLabel} vs {previousLabel}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left py-2 px-3 font-semibold" style={{ color: NAV_DARK_COLOR }}>Source</th>
              <th className="text-right py-2 px-3 font-semibold bg-amber-50" style={{ color: NAV_DARK_COLOR }}>Current</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: NAV_DARK_COLOR }}>Previous</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: NAV_DARK_COLOR }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              <>
                {tableData.map((row, idx) => (
                  <tr key={row.source} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} hover:bg-slate-100`}>
                    <td className="py-2 px-3 font-medium" style={{ color: NAV_DARK_COLOR }}>{row.label}</td>
                    <td className="text-right py-2 px-3 font-semibold bg-amber-50/50">{maskNumber(row.current)}</td>
                    <td className="text-right py-2 px-3 text-slate-500">{maskNumber(row.previous)}</td>
                    <td className="text-right py-2 px-3">{isPresentationMode ? <span className="text-slate-400">~</span> : <DeltaCell delta={row.delta} />}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                  <td className="py-2 px-3" style={{ color: NAV_DARK_COLOR }}>Total</td>
                  <td className="text-right py-2 px-3 bg-amber-100">{maskNumber(totalCurrent)}</td>
                  <td className="text-right py-2 px-3">{maskNumber(totalPrevious)}</td>
                  <td className="text-right py-2 px-3">{isPresentationMode ? <span className="text-slate-400">~</span> : <DeltaCell delta={calcDelta(totalCurrent, totalPrevious)} />}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-6 text-slate-400">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Bar Chart */}
      {chartData.length > 0 && (
        <div className="pt-2">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                tick={{ fontSize: 10, fill: NAV_DARK_COLOR }}
                tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                tick={{ fontSize: 10, fill: NAV_DARK_COLOR }} 
                width={85}
              />
              <Tooltip 
                contentStyle={{ fontSize: 11, color: NAV_DARK_COLOR }}
                formatter={(value) => [isPresentationMode ? maskNumber(value) : value.toLocaleString(), '']}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar 
                dataKey="current" 
                fill="#3B82F6" 
                name="Current" 
                radius={[0, 4, 4, 0]}
              />
              <Bar 
                dataKey="previous" 
                fill="#94A3B8" 
                name="Previous" 
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Main component with all regions
const RegionalTrafficTables = ({ 
  dateFilter, 
  excludeTurkey = true, 
  trafficDataSource = "ga4" 
}) => {
  const { sessionToken } = useAuth();
  const [comparisonType, setComparisonType] = useState("mom"); // Shared between all regions

  if (!dateFilter?.startDate || !dateFilter?.endDate) {
    return null;
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ color: NAV_DARK_COLOR }}>
            <BarChart3 className="w-5 h-5" />
            Regional Traffic Breakdown
            <span className={`text-xs px-2 py-0.5 rounded ml-1 ${
              trafficDataSource === "hubspot" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
            }`}>
              {trafficDataSource === "hubspot" ? "HubSpot" : "GA4"}
            </span>
          </CardTitle>
          <Tabs value={comparisonType} onValueChange={setComparisonType} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="mom" className="text-xs px-3">vs Previous Period</TabsTrigger>
              <TabsTrigger value="yoy" className="text-xs px-3">vs Same Period Last Year</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Traffic by source for each region (date filter applied, region filter independent)
        </p>
      </CardHeader>
      <CardContent>
        {/* Row 1: EMEA and North America */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* EMEA Panel */}
          <RegionPanel 
            region="emea"
            regionLabel="EMEA"
            dateFilter={dateFilter}
            excludeTurkey={excludeTurkey}
            trafficDataSource={trafficDataSource}
            sessionToken={sessionToken}
            comparisonType={comparisonType}
          />
          
          {/* NA Panel */}
          <RegionPanel 
            region="na"
            regionLabel="North America"
            dateFilter={dateFilter}
            excludeTurkey={excludeTurkey}
            trafficDataSource={trafficDataSource}
            sessionToken={sessionToken}
            comparisonType={comparisonType}
          />
        </div>
        
        {/* Row 2: LATAM and APAC */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LATAM Panel */}
          <RegionPanel 
            region="latam"
            regionLabel="Latin America"
            dateFilter={dateFilter}
            excludeTurkey={false}
            trafficDataSource={trafficDataSource}
            sessionToken={sessionToken}
            comparisonType={comparisonType}
          />
          
          {/* APAC Panel */}
          <RegionPanel 
            region="apac"
            regionLabel="Asia Pacific"
            dateFilter={dateFilter}
            excludeTurkey={false}
            trafficDataSource={trafficDataSource}
            sessionToken={sessionToken}
            comparisonType={comparisonType}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default RegionalTrafficTables;
